import { useState } from "react";
import { motion } from "framer-motion";
import { Seo } from "@/seo/Seo";
import { PageHero } from "@/layouts/PageHero";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { CtaButton } from "@/components/common/CtaButton";
import { ArticleCard } from "@/components/insights/ArticleCard";
import { DetailCta } from "@/components/detail/DetailCta";
import { articles, articleCategories } from "@/data/articles.data";
import { fadeUp, staggerContainer, viewportOnce } from "@/animations/motion";
import { cn } from "@/lib/utils";
import { siteConfig } from "@/config/site.config";

export default function Insights() {
  const [filter, setFilter] = useState("All");
  const categories = [
    { name: "All", count: articles.length },
    ...articleCategories(),
  ];
  const filtered =
    filter === "All" ? articles : articles.filter((a) => a.category === filter);

  return (
    <>
      <Seo
        title="Insights"
        description="Essays, playbooks and field notes from VibeWell on product design, engineering, AI, performance and building software that lasts."
        path="/insights"
      />
      <PageHero
        eyebrow="Insights"
        titleLines={["Expert Insights on AI,", "Digital Transformation and Enterprise Technology"]}
        description="Explore practical articles, implementation guides and technology insights to help organisations modernise operations and make informed digital transformation decisions."
      >
        <CtaButton to={siteConfig.primaryCta.path} withArrow magnetic>
          Book a Free Consultation
        </CtaButton>
      </PageHero>

      <Section spacing="compact" className="pt-0">
        <Container>
          {/* Category filter */}
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter articles">
            {categories.map((cat) => (
              <button
                key={cat.name}
                role="tab"
                aria-selected={filter === cat.name}
                data-testid={`insights-filter-${cat.name.toLowerCase()}`}
                onClick={() => setFilter(cat.name)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all duration-300",
                  filter === cat.name
                    ? "border-transparent bg-primary text-primary-foreground"
                    : "border-border bg-card text-foreground hover:border-primary/30",
                )}
              >
                {cat.name}
                <span
                  className={cn(
                    "rounded-full px-1.5 text-xs",
                    filter === cat.name ? "bg-white/20" : "bg-muted text-muted-foreground",
                  )}
                >
                  {cat.count}
                </span>
              </button>
            ))}
          </div>

          <motion.div
            key={filter}
            variants={staggerContainer(0.08, 0.05)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {filtered.map((article) => (
              <motion.div key={article.slug} variants={fadeUp}>
                <ArticleCard article={article} />
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      <DetailCta note="Enjoying the ideas? Let's put them to work on your product." />
    </>
  );
}
